import { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../constants/api";
import { HOSPITAL_STEPS, HOSPITAL_STEP_DESCRIPTIONS, HOSPITAL_MENU_GROUPS, getGroupSteps } from "../constants/menuConfig";
import { buildEstablishmentFormData, parseApiError } from "../utils/helpers";
import {
  mapEstablishmentToProfileForm,
  mergeProfileFormData,
} from "../utils/establishmentFormHelpers";
import { getStoredToken, getStoredUser, syncStoredUser } from "../auth/authStorage";
import { useAuth } from "./AuthContext";

const loadJson = async (url) => {
  try {
    const r = await fetch(url);
    if (!r.ok) return [];
    const data = await r.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

export function useHospitalPortalState() {
  const { user: authUser } = useAuth();
  const [portalUser, setPortalUser] = useState(authUser);
  const [currentStep, setCurrentStep] = useState(0);
  const [profileView, setProfileView] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ "hospital-menu": true });
  const [expandedSections, setExpandedSections] = useState({});
  const [establishment, setEstablishment] = useState(null);
  const [accreditations, setAccreditations] = useState([]);
  const [allEstablishments, setAllEstablishments] = useState([]);
  const [estTypes, setEstTypes] = useState([]);
  const [establishmentSubTypes, setEstablishmentSubTypes] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [specializationTypes, setSpecializationTypes] = useState([]);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [masterDataLoaded, setMasterDataLoaded] = useState(false);
  const [mediaGallery, setMediaGallery] = useState([]);
  const [mediaGalleryLoading, setMediaGalleryLoading] = useState(false);
  const [mediaGallerySaving, setMediaGallerySaving] = useState(false);
  const [awardsRecognition, setAwardsRecognition] = useState([]);
  const [awardsLoading, setAwardsLoading] = useState(false);
  const [awardsSaving, setAwardsSaving] = useState(false);
  const [newsArticles, setNewsArticles] = useState([]);
  const [newsArticlesLoading, setNewsArticlesLoading] = useState(false);
  const [newsArticlesSaving, setNewsArticlesSaving] = useState(false);
  const [digitalCme, setDigitalCme] = useState([]);
  const [digitalCmeLoading, setDigitalCmeLoading] = useState(false);
  const [digitalCmeSaving, setDigitalCmeSaving] = useState(false);
  const [medicalSpecialties, setMedicalSpecialties] = useState([]);

  const establishmentId = portalUser?.establishment_id ?? authUser?.establishment_id;

  const resolvePortalUser = useCallback(async () => {
    let currentUser = authUser || getStoredUser();
    if (!currentUser) return null;

    if (currentUser.establishment_id) {
      setPortalUser(currentUser);
      return currentUser;
    }

    const token = getStoredToken();
    if (token) {
      try {
        const r = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const data = await r.json();
          if (data.user) {
            setPortalUser(data.user);
            syncStoredUser(data.user);
            return data.user;
          }
        }
      } catch {
        // fall through to login lookup
      }
    }

    if (currentUser.login_id || currentUser.mobile_no) {
      try {
        const r = await fetch(`${API_BASE}/master/logins`);
        if (r.ok) {
          const logins = await r.json();
          const match = logins.find(
            (l) =>
              (currentUser.login_id && String(l.login_id) === String(currentUser.login_id)) ||
              (currentUser.mobile_no && l.mobile_no === currentUser.mobile_no)
          );
          if (match) {
            const updated = {
              ...currentUser,
              login_id: match.login_id,
              establishment_id: match.establishment_id,
              user_name: match.user_name ?? currentUser.user_name,
              email_address: match.email_address ?? currentUser.email_address,
            };
            setPortalUser(updated);
            syncStoredUser(updated);
            return updated;
          }
        }
      } catch {
        return currentUser;
      }
    }

    setPortalUser(currentUser);
    return currentUser;
  }, [authUser]);

  const fetchEstablishment = useCallback(async (estId = establishmentId) => {
    if (!estId) {
      setEstablishment(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/master/establishments/${estId}`);
      if (r.ok) {
        const data = await r.json();
        setEstablishment(data);
        return data;
      }
      setEstablishment(null);
      return null;
    } catch {
      setEstablishment(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [establishmentId]);

  useEffect(() => {
    let active = true;
    (async () => {
      const resolved = await resolvePortalUser();
      if (!active) return;
      if (resolved?.establishment_id) {
        await fetchEstablishment(resolved.establishment_id);
      } else {
        setLoading(false);
      }
    })();
    loadJson(`${API_BASE}/master/accreditations`).then(setAccreditations);
    return () => { active = false; };
  }, [resolvePortalUser, fetchEstablishment]);

  const fetchMediaGallery = useCallback(async (estId = establishmentId) => {
    if (!estId) {
      setMediaGallery([]);
      return [];
    }
    setMediaGalleryLoading(true);
    try {
      const r = await fetch(`${API_BASE}/master/media-gallery?establishment_id=${estId}`);
      if (!r.ok) {
        setMediaGallery([]);
        return [];
      }
      const data = await r.json();
      const rows = Array.isArray(data) ? data : [];
      setMediaGallery(rows);
      return rows;
    } catch {
      setMediaGallery([]);
      return [];
    } finally {
      setMediaGalleryLoading(false);
    }
  }, [establishmentId]);

  useEffect(() => {
    if (establishmentId) {
      fetchMediaGallery(establishmentId);
    }
  }, [establishmentId, establishment?.establishment_id, fetchMediaGallery]);

  const fetchAwardsRecognition = useCallback(async (estId = establishmentId) => {
    if (!estId) {
      setAwardsRecognition([]);
      return [];
    }
    setAwardsLoading(true);
    try {
      const r = await fetch(`${API_BASE}/master/awards-recognition?establishment_id=${estId}`);
      if (!r.ok) {
        setAwardsRecognition([]);
        return [];
      }
      const data = await r.json();
      const rows = Array.isArray(data) ? data : [];
      setAwardsRecognition(rows);
      return rows;
    } catch {
      setAwardsRecognition([]);
      return [];
    } finally {
      setAwardsLoading(false);
    }
  }, [establishmentId]);

  useEffect(() => {
    if (establishmentId) {
      fetchAwardsRecognition(establishmentId);
    }
  }, [establishmentId, establishment?.establishment_id, fetchAwardsRecognition]);

  const fetchNewsArticles = useCallback(async (estId = establishmentId) => {
    if (!estId) {
      setNewsArticles([]);
      return [];
    }
    setNewsArticlesLoading(true);
    try {
      const r = await fetch(`${API_BASE}/master/news-articles?establishment_id=${estId}`);
      if (!r.ok) {
        setNewsArticles([]);
        return [];
      }
      const data = await r.json();
      const rows = Array.isArray(data) ? data : [];
      setNewsArticles(rows);
      return rows;
    } catch {
      setNewsArticles([]);
      return [];
    } finally {
      setNewsArticlesLoading(false);
    }
  }, [establishmentId]);

  useEffect(() => {
    if (establishmentId) {
      fetchNewsArticles(establishmentId);
    }
  }, [establishmentId, establishment?.establishment_id, fetchNewsArticles]);

  const fetchDigitalCme = useCallback(async (estId = establishmentId) => {
    if (!estId) {
      setDigitalCme([]);
      return [];
    }
    setDigitalCmeLoading(true);
    try {
      const r = await fetch(`${API_BASE}/master/digital-cme?establishment_id=${estId}`);
      if (!r.ok) {
        setDigitalCme([]);
        return [];
      }
      const data = await r.json();
      const rows = Array.isArray(data) ? data : [];
      setDigitalCme(rows);
      return rows;
    } catch {
      setDigitalCme([]);
      return [];
    } finally {
      setDigitalCmeLoading(false);
    }
  }, [establishmentId]);

  useEffect(() => {
    if (establishmentId) {
      fetchDigitalCme(establishmentId);
    }
  }, [establishmentId, establishment?.establishment_id, fetchDigitalCme]);

  useEffect(() => {
    loadJson(`${API_BASE}/master/specialties`).then(setMedicalSpecialties);
  }, []);

  const loadProfileMasterData = useCallback(async () => {
    if (masterDataLoaded) return;
    const [ests, estTypeRows, establishmentSubTypeRows, locTypes, locs, depts, specs, accrs] = await Promise.all([
      loadJson(`${API_BASE}/master/establishments`),
      loadJson(`${API_BASE}/master/establishment-types`),
      loadJson(`${API_BASE}/master/establishment-sub-types`),
      loadJson(`${API_BASE}/locations/types`),
      loadJson(`${API_BASE}/locations`),
      loadJson(`${API_BASE}/master/departments`),
      loadJson(`${API_BASE}/master/specialization-types`),
      loadJson(`${API_BASE}/master/accreditations`),
    ]);
    setAllEstablishments(ests);
    setEstTypes(estTypeRows);
    setEstablishmentSubTypes(establishmentSubTypeRows);
    setLocationTypes(locTypes);
    setLocations(locs);
    setDepartments(depts);
    setSpecializationTypes(specs);
    setAccreditations(accrs);
    setMasterDataLoaded(true);
  }, [masterDataLoaded]);

  useEffect(() => {
    if (profileView !== "create" || !establishment?.establishment_id) return;
    setProfileForm((prev) => {
      const hasBasics = prev.establishment_name && prev.establishment_type_id;
      if (
        hasBasics &&
        String(prev.establishment_id) === String(establishment.establishment_id)
      ) {
        return prev;
      }
      return mapEstablishmentToProfileForm(establishment);
    });
  }, [profileView, establishment]);

  const openCreateProfile = async () => {
    await loadProfileMasterData();
    let resolvedUser = portalUser || authUser;
    if (!resolvedUser?.establishment_id) {
      resolvedUser = await resolvePortalUser();
    }
    const estId = resolvedUser?.establishment_id;
    if (!estId) {
      setProfileForm({});
      setProfileMsg("Error: No establishment linked to this login. Ask admin to create login with establishment.");
      setProfileView("create");
      return;
    }

    const cached =
      establishment && String(establishment.establishment_id) === String(estId)
        ? establishment
        : null;
    if (cached) {
      setProfileForm(mapEstablishmentToProfileForm(cached));
      setProfileView("create");
      setProfileMsg("");
    }

    try {
      const r = await fetch(`${API_BASE}/master/establishments/${estId}`);
      if (!r.ok) throw new Error("Could not load establishment record.");
      const est = await r.json();
      setEstablishment(est);
      setProfileForm(mapEstablishmentToProfileForm(est));
      setProfileMsg("");
      setProfileView("create");
    } catch (e) {
      if (!cached) {
        setProfileForm({});
        setProfileMsg(`Error: ${e.message}`);
        setProfileView("create");
      } else {
        setProfileMsg(`Warning: Showing saved profile. Could not refresh: ${e.message}`);
      }
    }
  };

  const closeCreateProfile = () => {
    setProfileView("overview");
    setProfileMsg("");
  };

  const handleProfileChange = (field, value) => {
    setProfileForm((p) => ({ ...p, [field]: value }));
  };

  const handleSaveProfile = async () => {
    const id = profileForm.establishment_id || establishmentId;
    if (!id) {
      setProfileMsg("Error: No establishment linked to this login.");
      return;
    }
    const payload = mergeProfileFormData(establishment, profileForm);
    if (!payload.establishment_name?.trim() || !payload.establishment_type_id) {
      setProfileMsg("Error: Brand name and hospital type are required.");
      return;
    }
    setProfileSaving(true);
    setProfileMsg("");
    try {
      const fd = buildEstablishmentFormData(payload);
      const r = await fetch(`${API_BASE}/master/establishments/${id}`, { method: "PUT", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));
      const refreshed = await fetchEstablishment(id);
      if (refreshed) {
        setProfileForm(mapEstablishmentToProfileForm(refreshed));
      }
      setProfileView("overview");
      setCurrentStep(0);
      setProfileMsg("Profile saved successfully!");
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
    } finally {
      setProfileSaving(false);
    }
  };

  const saveMediaGalleryItem = async (payload, files = null, editId = null) => {
    const estId = establishmentId || profileForm.establishment_id;
    if (!estId) {
      setProfileMsg("Error: No establishment linked to this login.");
      return false;
    }

    const fileList = Array.isArray(files) ? files : files ? [files] : [];
    if (payload.media_type === "Image" && !editId && fileList.length === 0) {
      setProfileMsg("Error: Please select at least one image.");
      return false;
    }

    setMediaGallerySaving(true);
    setProfileMsg("");
    try {
      const uploadOne = async (file, id = null) => {
        const fd = new FormData();
        fd.append("media_type", payload.media_type);
        fd.append("establishment_id", String(estId));
        if (payload.media_date) fd.append("media_date", payload.media_date);
        if (payload.place) fd.append("place", payload.place);
        if (payload.short_note) fd.append("short_note", payload.short_note);
        if (payload.media_type === "Video" && payload.media_link) {
          fd.append("media_link", payload.media_link);
        }
        if (payload.media_type === "Image" && file) {
          fd.append("media_upload", file);
        }

        const url = id
          ? `${API_BASE}/master/media-gallery/${id}`
          : `${API_BASE}/master/media-gallery`;
        const r = await fetch(url, { method: id ? "PUT" : "POST", body: fd });
        if (!r.ok) throw new Error(await parseApiError(r));
      };

      if (editId) {
        await uploadOne(fileList[0] || null, editId);
      } else if (payload.media_type === "Image") {
        for (const file of fileList) {
          await uploadOne(file);
        }
      } else {
        await uploadOne(null);
      }

      await fetchMediaGallery(estId);
      const count = payload.media_type === "Image" && !editId ? fileList.length : 1;
      setProfileMsg(
        editId
          ? "Gallery item updated."
          : count > 1
            ? `${count} images uploaded.`
            : "Gallery item added."
      );
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setMediaGallerySaving(false);
    }
  };

  const saveAwardRecognition = async (payload, files = [], editId = null, removedImageIds = []) => {
    const estId = establishmentId || profileForm.establishment_id;
    if (!estId) {
      setProfileMsg("Error: No establishment linked to this login.");
      return false;
    }
    if (!payload.award_title?.trim()) {
      setProfileMsg("Error: Award title is required.");
      return false;
    }

    setAwardsSaving(true);
    setProfileMsg("");
    try {
      const fd = new FormData();
      fd.append("establishment_id", String(estId));
      fd.append("award_title", payload.award_title.trim());
      if (payload.awarding_body) fd.append("awarding_body", payload.awarding_body);
      if (payload.award_date) fd.append("award_date", payload.award_date);
      if (payload.place) fd.append("place", payload.place);
      if (payload.short_note) fd.append("short_note", payload.short_note);
      if (editId && removedImageIds.length) {
        fd.append("removed_image_ids", JSON.stringify(removedImageIds));
      }
      files.forEach((file) => fd.append("award_images", file));

      const url = editId
        ? `${API_BASE}/master/awards-recognition/${editId}`
        : `${API_BASE}/master/awards-recognition`;
      const r = await fetch(url, { method: editId ? "PUT" : "POST", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));

      await fetchAwardsRecognition(estId);
      setProfileMsg(editId ? "Award updated." : "Award saved.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setAwardsSaving(false);
    }
  };

  const deleteAwardRecognition = async (awardId) => {
    setAwardsSaving(true);
    setProfileMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/awards-recognition/${awardId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      await fetchAwardsRecognition(establishmentId);
      setProfileMsg("Award deleted.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setAwardsSaving(false);
    }
  };

  const saveNewsArticle = async (
    payload,
    referenceFiles = [],
    galleryFiles = [],
    editId = null,
    removedReferenceIds = [],
    removedImageIds = []
  ) => {
    const estId = establishmentId || profileForm.establishment_id;
    if (!estId) {
      setProfileMsg("Error: No establishment linked to this login.");
      return false;
    }
    if (!payload.article_title?.trim()) {
      setProfileMsg("Error: Article title is required.");
      return false;
    }

    setNewsArticlesSaving(true);
    setProfileMsg("");
    try {
      const fd = new FormData();
      fd.append("establishment_id", String(estId));
      fd.append("article_title", payload.article_title.trim());
      if (payload.context_tags) fd.append("context_tags", payload.context_tags);
      if (payload.media_link) fd.append("media_link", payload.media_link);
      if (payload.short_description) fd.append("short_description", payload.short_description);
      if (payload.publication_name) fd.append("publication_name", payload.publication_name);
      if (payload.publication_date) fd.append("publication_date", payload.publication_date);
      if (editId && removedReferenceIds.length) {
        fd.append("removed_reference_ids", JSON.stringify(removedReferenceIds));
      }
      if (editId && removedImageIds.length) {
        fd.append("removed_image_ids", JSON.stringify(removedImageIds));
      }
      referenceFiles.forEach((file) => fd.append("reference_pdfs", file));
      galleryFiles.forEach((file) => fd.append("gallery_images", file));

      const url = editId
        ? `${API_BASE}/master/news-articles/${editId}`
        : `${API_BASE}/master/news-articles`;
      const r = await fetch(url, { method: editId ? "PUT" : "POST", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));

      await fetchNewsArticles(estId);
      setProfileMsg(editId ? "News article updated." : "News article saved.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setNewsArticlesSaving(false);
    }
  };

  const saveDigitalCme = async (
    payload,
    referenceFiles = [],
    editId = null,
    removedReferenceIds = []
  ) => {
    const estId = establishmentId || profileForm.establishment_id;
    if (!estId) {
      setProfileMsg("Error: No establishment linked to this login.");
      return false;
    }
    if (!payload.cme_title?.trim()) {
      setProfileMsg("Error: CME title is required.");
      return false;
    }

    setDigitalCmeSaving(true);
    setProfileMsg("");
    try {
      const specialtyIds = payload.medical_specialty_ids
        ? payload.medical_specialty_ids.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

      const fd = new FormData();
      fd.append("establishment_id", String(estId));
      fd.append("cme_title", payload.cme_title.trim());
      if (payload.doctor_id) fd.append("doctor_id", payload.doctor_id);
      if (payload.context_tags) fd.append("context_tags", payload.context_tags);
      if (payload.media_link) fd.append("media_link", payload.media_link);
      if (payload.short_description) fd.append("short_description", payload.short_description);
      if (payload.long_description) fd.append("long_description", payload.long_description);
      if (payload.duration_minutes) fd.append("duration_minutes", payload.duration_minutes);
      fd.append("medical_specialty_ids", JSON.stringify(specialtyIds));
      if (editId && removedReferenceIds.length) {
        fd.append("removed_reference_ids", JSON.stringify(removedReferenceIds));
      }
      referenceFiles.forEach((file) => fd.append("reference_pdfs", file));

      const url = editId
        ? `${API_BASE}/master/digital-cme/${editId}`
        : `${API_BASE}/master/digital-cme`;
      const r = await fetch(url, { method: editId ? "PUT" : "POST", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));

      await fetchDigitalCme(estId);
      setProfileMsg(editId ? "Digital CME updated." : "Digital CME saved.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setDigitalCmeSaving(false);
    }
  };

  const deleteDigitalCme = async (cmeId) => {
    setDigitalCmeSaving(true);
    setProfileMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/digital-cme/${cmeId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      await fetchDigitalCme(establishmentId);
      setProfileMsg("Digital CME deleted.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setDigitalCmeSaving(false);
    }
  };

  const deleteNewsArticle = async (articleId) => {
    setNewsArticlesSaving(true);
    setProfileMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/news-articles/${articleId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      await fetchNewsArticles(establishmentId);
      setProfileMsg("News article deleted.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setNewsArticlesSaving(false);
    }
  };

  const deleteMediaGalleryItem = async (mediaId) => {
    setMediaGallerySaving(true);
    setProfileMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/media-gallery/${mediaId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      await fetchMediaGallery(establishmentId);
      setProfileMsg("Gallery item deleted.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setMediaGallerySaving(false);
    }
  };

  const toggleMenuGroup = (groupId) => {
    const isOpen = !!expandedGroups[groupId];
    if (isOpen) {
      setExpandedGroups({});
      setExpandedSections((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith(`${groupId}:`)))
      );
    } else {
      setExpandedGroups({ [groupId]: true });
      setExpandedSections({});
    }
  };

  const toggleMenuSection = (groupId, sectionId) => {
    const key = `${groupId}:${sectionId}`;
    setExpandedSections((prev) => {
      const isOpen = !!prev[key];
      if (isOpen) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { [key]: true };
    });
  };

  const selectStep = (stepIndex) => {
    setProfileView("overview");
    setCurrentStep(stepIndex);
    setSidebarOpen(false);
    const parentGroup = HOSPITAL_MENU_GROUPS.find((g) => getGroupSteps(g).includes(stepIndex));
    if (parentGroup) {
      setExpandedGroups({ [parentGroup.id]: true });
      const activeSection = parentGroup.sections?.find((section) => section.step === stepIndex);
      if (activeSection) {
        setExpandedSections({ [`${parentGroup.id}:${activeSection.id}`]: true });
      }
    }
  };

  const updatePortalUser = useCallback((patch) => {
    setPortalUser((prev) => {
      const base = prev || authUser || {};
      const next = typeof patch === "function" ? patch(base) : { ...base, ...patch };
      syncStoredUser(next);
      return next;
    });
  }, [authUser]);

  return {
    user: portalUser || authUser,
    currentStep,
    profileView,
    sidebarOpen,
    setSidebarOpen,
    expandedGroups,
    expandedSections,
    toggleMenuGroup,
    toggleMenuSection,
    selectStep,
    establishment,
    establishments: establishment ? [establishment] : [],
    allEstablishments,
    accreditations,
    estTypes,
    establishmentSubTypes,
    locationTypes,
    locations,
    departments,
    specializationTypes,
    profileForm,
    profileSaving,
    profileMsg,
    loading,
    mediaGallery,
    mediaGalleryLoading,
    mediaGallerySaving,
    fetchMediaGallery,
    saveMediaGalleryItem,
    deleteMediaGalleryItem,
    awardsRecognition,
    awardsLoading,
    awardsSaving,
    fetchAwardsRecognition,
    saveAwardRecognition,
    deleteAwardRecognition,
    newsArticles,
    newsArticlesLoading,
    newsArticlesSaving,
    fetchNewsArticles,
    saveNewsArticle,
    deleteNewsArticle,
    digitalCme,
    digitalCmeLoading,
    digitalCmeSaving,
    medicalSpecialties,
    fetchDigitalCme,
    saveDigitalCme,
    deleteDigitalCme,
    openCreateProfile,
    closeCreateProfile,
    handleProfileChange,
    handleSaveProfile,
    STEPS: HOSPITAL_STEPS,
    STEP_DESCRIPTIONS: HOSPITAL_STEP_DESCRIPTIONS,
    MENU_GROUPS: HOSPITAL_MENU_GROUPS,
    refetchEstablishment: fetchEstablishment,
    updatePortalUser,
  };
}
